import React, { ReactElement, useMemo } from 'react';
import { OTPInputBox, OTPInputContainer } from './OTPInput.styles';

const RE_DIGIT = new RegExp(/^\d+$/);

export function OTPInput({
    value,
    value_length,
    onChange,
}: {
    value: string;
    value_length: number;
    onChange: (value: string) => void;
}): ReactElement {
    const value_items = useMemo(() => {
        const value_array = value.split('');
        const items: Array<string> = [];

        for (let i = 0; i < value_length; i++) {
            const char = value_array[i];

            if (RE_DIGIT.test(char)) {
                items.push(char);
            } else {
                items.push('');
            }
        }

        return items;
    }, [value, value_length]);

    function focusToNextInput(target: HTMLElement): void {
        const next_element_sibling = target.nextElementSibling as HTMLInputElement | null;

        if (next_element_sibling) {
            next_element_sibling.focus();
        }
    }

    function focusToPrevInput(target: HTMLElement): void {
        const previous_element_sibling = target.previousElementSibling as HTMLInputElement | null;

        if (previous_element_sibling) {
            previous_element_sibling.focus();
        }
    }

    function inputOnChange(e: React.ChangeEvent<HTMLInputElement>, idx: number): void {
        const target = e.target;
        let target_value = target.value.trim();
        const is_target_value_digit = RE_DIGIT.test(target_value);

        if (!is_target_value_digit && target_value !== '') {
            return;
        }

        const next_input_el = target.nextElementSibling as HTMLInputElement | null;

        // only delete digit if next input element has no value
        if (!is_target_value_digit && next_input_el && next_input_el.value !== '') {
            return;
        }

        target_value = is_target_value_digit ? target_value : ' ';

        const target_value_length = target_value.length;

        if (target_value_length === 1) {
            const new_value = value.substring(0, idx) + target_value + value.substring(idx + 1);

            onChange(new_value);

            if (!is_target_value_digit) {
                return;
            }

            focusToNextInput(target);
        } else if (target_value_length === value_length) {
            onChange(target_value);

            target.blur();
        }
    }

    function inputOnKeyDown(e: React.KeyboardEvent<HTMLInputElement>): void {
        const { key } = e;
        const target = e.target as HTMLInputElement;

        if (key === 'ArrowRight' || key === 'ArrowDown') {
            e.preventDefault();

            return focusToNextInput(target);
        }

        if (key === 'ArrowLeft' || key === 'ArrowUp') {
            e.preventDefault();

            return focusToPrevInput(target);
        }

        const target_value = target.value;

        // keep the selection range position
        // if the same digit was typed
        target.setSelectionRange(0, target_value.length);

        if (e.key !== 'Backspace' || target_value !== '') {
            return;
        }

        focusToPrevInput(target);
    }

    function inputOnFocus(e: React.FocusEvent<HTMLInputElement>): void {
        const { target } = e;

        const prev_input_el = target.previousElementSibling as HTMLInputElement | null;

        if (prev_input_el && prev_input_el.value === '') {
            return prev_input_el.focus();
        }

        target.setSelectionRange(0, target.value.length);
    }

    return (
        <OTPInputContainer>
            {value_items.map((digit, idx) => (
                <OTPInputBox
                    key={idx}
                    name={`otp-${idx}`}
                    type='text'
                    inputMode='numeric'
                    autoComplete='one-time-code'
                    pattern='\d{1}'
                    maxLength={value_length}
                    value={digit}
                    onChange={(e): void => inputOnChange(e, idx)}
                    onKeyDown={inputOnKeyDown}
                    onFocus={inputOnFocus}
                />
            ))}
        </OTPInputContainer>
    );
}
